/**
 * The one owner of an app's integrations data on the Apps page.
 *
 * Three places read the same `GET /applications/:id/integrations` reply:
 * the one-line summary in the app card, the Manage side panel with the
 * full cards, and the links on each variant row. Before, the reply lived
 * inside the Integrations tab component, where neither the summary nor
 * the variant rows could reach it. A provider per app card holds it once
 * and each reader takes it from context, so the three can never show two
 * different answers.
 *
 * The data changes in three places, and nowhere else:
 *   1. A GitHub or Slack save in the panel writes the save's reply back
 *      through `update`, as the tab did.
 *   2. The issue tracker card reports its save and its remove the same way.
 *   3. Closing a variant drawer, by any means, loads the data again, so
 *      the rule and override counts on the variant rows are fresh.
 * Loading again when the panel closes was rejected: the summary would show
 * the old status for as long as the panel stays open.
 *
 * Panel and drawer state live in the URL, so a link can open either and the
 * back button closes them. `?tab=integrations` opens the panel. It is the
 * old Integrations tab's link, kept so links built elsewhere still work.
 * `?variantConfig=<variant>&variantConfigKind=trigger|slack|pipeline` opens
 * one variant's drawer.
 *
 * Lives beside the panel, not in `hooks/`: only the Apps page reads it.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { applicationsAPI, type AppIntegrationsResponse } from '@/lib/api'
import { useAppContext } from '@/hooks/useAppContext'
import { VariantTriggerRulesDrawer } from './VariantTriggerRulesDrawer'
import { VariantNotificationOverridesDrawer } from './VariantNotificationOverridesDrawer'
import { VariantPipelineIntegrationDrawer } from './VariantPipelineIntegrationDrawer'

export type VariantDrawerKind = 'trigger' | 'slack' | 'pipeline'

export interface AppIntegrationsState {
  appId: string
  data: AppIntegrationsResponse | null
  loading: boolean
  loadError: string | null
  /** Write a save's reply back, so every reader shows it at once. */
  update: (change: (prev: AppIntegrationsResponse) => AppIntegrationsResponse) => void
  panelOpen: boolean
  openPanel: () => void
  closePanel: () => void
  openDrawer: (variantId: string, kind: VariantDrawerKind) => void
}

const AppIntegrationsContext = createContext<AppIntegrationsState | null>(null)

/** Read the app's integrations from the nearest `AppIntegrationsProvider`. */
export function useAppIntegrations(): AppIntegrationsState {
  const ctx = useContext(AppIntegrationsContext)
  if (!ctx) throw new Error('useAppIntegrations must be used within AppIntegrationsProvider')
  return ctx
}

/** Sanitize the drawer kind against the known values, so a crafted URL
 * (`variantConfigKind=foo`) can't leave `?variantConfig=` set with no drawer
 * and nothing to close. Missing or unknown falls back to `trigger`, the
 * most common entry point and the value links written before the kind
 * existed relied on. Exported for unit tests. */
export function parseDrawerKind(raw: string | null): VariantDrawerKind {
  return raw === 'slack' ? 'slack' : raw === 'pipeline' ? 'pipeline' : 'trigger'
}

interface AppIntegrationsProviderProps {
  appId: string
  /** Changes whenever the app's variants change (added, renamed,
   * removed), so the per-variant counts are loaded again. Undefined until
   * the variants have loaded: their first arrival is not a change, so it
   * does not load the integrations a second time. */
  refreshKey?: string
  children: ReactNode
}

export function AppIntegrationsProvider({ appId, refreshKey, children }: AppIntegrationsProviderProps) {
  const { selectedAppId, allVariants } = useAppContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<AppIntegrationsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Bumped to load again: when a variant drawer closes (refresh point 3)
  // or the variant list changes. A reload keeps the data on screen
  // instead of flashing "Loading".
  const [reloadCount, setReloadCount] = useState(0)

  // The first variant list seen is the one the first load already
  // counted; only a later, different list loads again.
  const seenRefreshKey = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (refreshKey === undefined) return
    if (seenRefreshKey.current === undefined) {
      seenRefreshKey.current = refreshKey
      return
    }
    if (seenRefreshKey.current !== refreshKey) {
      seenRefreshKey.current = refreshKey
      setReloadCount((n) => n + 1)
    }
  }, [refreshKey])

  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    applicationsAPI
      .getIntegrations(appId)
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appId, reloadCount])

  // A different app starts from "Loading", never from the last app's data.
  useEffect(() => {
    setData(null)
    setLoading(true)
  }, [appId])

  const update = useCallback((change: (prev: AppIntegrationsResponse) => AppIntegrationsResponse) => {
    setData((prev) => (prev ? change(prev) : prev))
  }, [])

  // A drawer opens only for one of this app's variants, so a second card
  // on screen never opens a copy of it. A trigger rules drawer also needs
  // GitHub: the link that opens it is behind the same check, and this stops
  // a stale URL reopening it after a disconnect.
  const openVariantId = searchParams.get('variantConfig')
  const openKind = parseDrawerKind(searchParams.get('variantConfigKind'))
  const ownsOpenVariant =
    !!openVariantId && allVariants.some((v) => v.id === openVariantId && v.applicationId === appId)
  const shownVariantId =
    ownsOpenVariant && data && (openKind !== 'trigger' || data.github.connected) ? openVariantId : null

  // However a variant drawer closes (its Close button, Escape, the browser's
  // Back button), load the data again so the counts on the rows are fresh
  // (refresh point 3). Watching the address catches all three; a handler on
  // the Close button alone missed Back.
  const lastShownVariantId = useRef<string | null>(null)
  useEffect(() => {
    if (lastShownVariantId.current && !shownVariantId) setReloadCount((n) => n + 1)
    lastShownVariantId.current = shownVariantId
  }, [shownVariantId])

  // The panel answers only for the selected app, and only once a `?app=`
  // link has been applied (the page strips `app` after selecting it).
  // Otherwise, with several cards on screen, every card would open its
  // own panel from the one `tab` value. It also steps aside while a
  // variant drawer is open: two drawers would each catch Escape and keep
  // keyboard focus, so a link that names both shows the variant's drawer
  // first and the panel once it closes.
  const panelOpen =
    searchParams.get('tab') === 'integrations' &&
    !searchParams.has('app') &&
    selectedAppId === appId &&
    !shownVariantId

  const openPanel = useCallback(() => {
    const params = new URLSearchParams(searchParams)
    params.set('tab', 'integrations')
    // A card for an app that isn't selected selects it first, through
    // the page's existing `?app=` link handling.
    if (selectedAppId !== appId) params.set('app', appId)
    setSearchParams(params, { replace: false })
  }, [searchParams, setSearchParams, selectedAppId, appId])

  const closePanel = useCallback(() => {
    const params = new URLSearchParams(searchParams)
    params.delete('tab')
    setSearchParams(params, { replace: false })
  }, [searchParams, setSearchParams])

  const openDrawer = useCallback(
    (variantId: string, kind: VariantDrawerKind) => {
      const params = new URLSearchParams(searchParams)
      params.set('variantConfig', variantId)
      params.set('variantConfigKind', kind)
      setSearchParams(params, { replace: false })
    },
    [searchParams, setSearchParams],
  )

  const closeDrawer = useCallback(() => {
    const params = new URLSearchParams(searchParams)
    params.delete('variantConfig')
    params.delete('variantConfigKind')
    setSearchParams(params, { replace: false })
  }, [searchParams, setSearchParams])

  const value = useMemo<AppIntegrationsState>(
    () => ({ appId, data, loading, loadError, update, panelOpen, openPanel, closePanel, openDrawer }),
    [appId, data, loading, loadError, update, panelOpen, openPanel, closePanel, openDrawer],
  )

  // A link naming another app's variant, opened while this app is the
  // selected one (so this is the only card on screen): no card will open
  // it, so drop it from the URL rather than leave it stranded. With no app
  // selected, every card is on screen and the owning card opens it.
  const strandedVariantId =
    openVariantId && selectedAppId === appId
      ? allVariants.find((v) => v.id === openVariantId && v.applicationId !== appId)?.id
      : undefined
  useEffect(() => {
    if (!strandedVariantId) return
    const params = new URLSearchParams(searchParams)
    params.delete('variantConfig')
    params.delete('variantConfigKind')
    setSearchParams(params, { replace: true })
  }, [strandedVariantId, searchParams, setSearchParams])

  return (
    <AppIntegrationsContext.Provider value={value}>
      {children}
      {shownVariantId && data && (
        <>
          {openKind === 'trigger' && (
            <VariantTriggerRulesDrawer
              appId={appId}
              variantId={shownVariantId}
              githubRepoIdentifier={data.bindings.github?.repoIdentifier}
              onClose={closeDrawer}
            />
          )}
          {openKind === 'slack' && (
            <VariantNotificationOverridesDrawer appId={appId} variantId={shownVariantId} onClose={closeDrawer} />
          )}
          {openKind === 'pipeline' && (
            <VariantPipelineIntegrationDrawer appId={appId} variantId={shownVariantId} onClose={closeDrawer} />
          )}
        </>
      )}
    </AppIntegrationsContext.Provider>
  )
}
